import {
  AudioInputDevice,
  ConsoleLogger,
  DefaultDeviceController,
  DefaultMeetingSession,
  LogLevel,
  MeetingSession,
  MeetingSessionConfiguration,
  VoiceFocusDeviceTransformer,
} from 'amazon-chime-sdk-js';
import { ConnectionData } from '@/types/connect.types';
import { debug, isDebug } from '@/config/constants';

class ChimeService {
  private voiceFocusTransformer: VoiceFocusDeviceTransformer | null = null;
  private voiceFocusSupported: boolean | null = null;
  private warmupPromise: Promise<void> | null = null;

  /**
   * Pre-warm Voice Focus on page load so it's ready when a call starts.
   * Skips iOS Safari (blob Worker not supported).
   * Safe to call multiple times — only runs once.
   */
  warmupVoiceFocus(): void {
    if (this.warmupPromise) return; // Already warming up

    const isIOSSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) && 
      !('MSStream' in window);
    if (isIOSSafari) {
      debug.log('iOS Safari detected, skipping Voice Focus warmup');
      this.voiceFocusSupported = false;
      return;
    }

    this.warmupPromise = (async () => {
      try {
        // Check support with timeout
        const supportCheck = VoiceFocusDeviceTransformer.isSupported();
        const supportTimeout = new Promise<boolean>((resolve) => 
          setTimeout(() => resolve(false), 3000)
        );
        this.voiceFocusSupported = await Promise.race([supportCheck, supportTimeout]);

        if (this.voiceFocusSupported) {
          debug.log('Pre-warming Amazon Voice Focus...');
          const createPromise = VoiceFocusDeviceTransformer.create();
          const timeoutPromise = new Promise<null>((_, reject) => 
            setTimeout(() => reject(new Error('Voice Focus warmup timed out')), 5000)
          );
          this.voiceFocusTransformer = await Promise.race([createPromise, timeoutPromise]) as VoiceFocusDeviceTransformer;
          debug.log('✓ Voice Focus pre-warmed');
        } else {
          debug.log('Voice Focus not supported on this browser');
        }
      } catch (error) {
        debug.warn('Voice Focus warmup failed:', error);
        this.voiceFocusSupported = false;
        this.voiceFocusTransformer = null;
      }
    })();
  }

  async createSession(
    connectionData: ConnectionData,
    logLevel: LogLevel = LogLevel.WARN
  ): Promise<MeetingSession> {
    try {
      debug.log('Creating Chime session with connection data:', {
        hasMeeting: !!connectionData.Meeting,
        hasAttendee: !!connectionData.Attendee,
        meetingId: connectionData.Meeting?.MeetingId,
        attendeeId: connectionData.Attendee?.AttendeeId,
        mediaRegion: (connectionData.Meeting as Record<string, unknown>)?.MediaRegion,
        mediaPlacement: connectionData.Meeting?.MediaPlacement ? 'present' : 'missing'
      });

      const logger = new ConsoleLogger('ChimeSDK', logLevel);
      const deviceController = new DefaultDeviceController(logger, { enableWebAudio: true });
      const configuration = new MeetingSessionConfiguration(
        connectionData.Meeting,
        connectionData.Attendee
      );

      debug.log('Chime configuration created:', {
        meetingId: configuration.meetingId,
        externalMeetingId: configuration.externalMeetingId,
        urls: configuration.urls ? 'present' : 'missing'
      });

      const meetingSession = new DefaultMeetingSession(configuration, logger, deviceController);
      debug.log('✓ Chime session created');
      return meetingSession;
    } catch (error) {
      console.error('✗ Failed to create Chime session:', error);
      console.error('Connection data that failed:', JSON.stringify(connectionData, null, 2));
      throw error;
    }
  }

  async startAudio(session: MeetingSession, enableVoiceFocus: boolean = true): Promise<void> {
    try {
      const t0 = performance.now();
      debug.log('Listing audio input devices...');
      const audioInputDevices = await session.audioVideo.listAudioInputDevices();
      const tDevices = performance.now();
      debug.log(`Found ${audioInputDevices.length} audio input device(s)`);
      
      if (audioInputDevices.length > 0) {
        let deviceToUse: AudioInputDevice = audioInputDevices[0].deviceId;
        
        const isIOSSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) && 
          !('MSStream' in window);
        if (isIOSSafari) {
          debug.log('iOS Safari detected, skipping Voice Focus (blob Worker not supported)');
        }
        if (enableVoiceFocus && !isIOSSafari) {
          try {
            // Wait for warmup to complete if it's still running
            if (this.warmupPromise) {
              await this.warmupPromise;
            }
            
            if (this.voiceFocusTransformer) {
              const voiceFocusDevice = await this.voiceFocusTransformer.createTransformDevice(deviceToUse as string);
              if (voiceFocusDevice) {
                deviceToUse = voiceFocusDevice;
                debug.log('✓ Amazon Voice Focus enabled');
              }
            } else if (this.voiceFocusSupported === null) {
              // Warmup wasn't called — fall back to inline init
              debug.log('Voice Focus not pre-warmed, initializing inline...');
              let isSupported = false;
              try {
                const supportCheck = VoiceFocusDeviceTransformer.isSupported();
                const supportTimeout = new Promise<boolean>((resolve) => 
                  setTimeout(() => resolve(false), 3000)
                );
                isSupported = await Promise.race([supportCheck, supportTimeout]);
              } catch {
                isSupported = false;
              }
              
              if (isSupported) {
                const createPromise = VoiceFocusDeviceTransformer.create();
                const timeoutPromise = new Promise<null>((_, reject) => 
                  setTimeout(() => reject(new Error('Voice Focus initialization timed out')), 5000)
                );
                this.voiceFocusTransformer = await Promise.race([createPromise, timeoutPromise]) as VoiceFocusDeviceTransformer;
                
                if (this.voiceFocusTransformer) {
                  const voiceFocusDevice = await this.voiceFocusTransformer.createTransformDevice(deviceToUse as string);
                  if (voiceFocusDevice) {
                    deviceToUse = voiceFocusDevice;
                    debug.log('✓ Amazon Voice Focus enabled (inline init)');
                  }
                }
              }
            }
          } catch (voiceFocusError) {
            debug.warn('Voice Focus not available, using standard audio:', voiceFocusError);
            this.voiceFocusTransformer = null;
            deviceToUse = audioInputDevices[0].deviceId;
          }
        }
        
        debug.log('Starting audio input...');
        try {
          const startInputPromise = session.audioVideo.startAudioInput(deviceToUse);
          const startInputTimeout = new Promise<void>((_, reject) =>
            setTimeout(() => reject(new Error('startAudioInput timed out after 10s')), 10000)
          );
          await Promise.race([startInputPromise, startInputTimeout]);
        } catch (inputError) {
          if (deviceToUse !== audioInputDevices[0].deviceId) {
            debug.warn('Voice Focus audio input failed, falling back to standard audio:', inputError);
            this.voiceFocusTransformer = null;
            await session.audioVideo.startAudioInput(audioInputDevices[0].deviceId);
          } else {
            throw inputError;
          }
        }
        debug.log('✓ Audio input started');
      }

      const tInput = performance.now();
      const audioOutputElement = document.getElementById('audio-output') as HTMLAudioElement;
      if (audioOutputElement) {
        session.audioVideo.bindAudioElement(audioOutputElement);
      }

      session.audioVideo.start();
      const tStart = performance.now();
      if (isDebug()) console.log(`⏱ Audio Setup - Devices: ${Math.round(tDevices - t0)}ms, Voice Focus + Input: ${Math.round(tInput - tDevices)}ms, Bind + Start: ${Math.round(tStart - tInput)}ms, Total: ${Math.round(tStart - t0)}ms`);
      debug.log('✓ Audio started');
    } catch (error) {
      console.error('✗ Failed to start audio:', error);
      throw error;
    }
  }

  stopAudio(session: MeetingSession): void {
    try {
      session.audioVideo.stop();
      debug.log('✓ Audio stopped');
    } catch (error) {
      console.error('✗ Failed to stop audio:', error);
    }
  }

  muteAudio(session: MeetingSession): void {
    try {
      session.audioVideo.realtimeMuteLocalAudio();
      debug.log('✓ Audio muted');
    } catch (error) {
      console.error('✗ Failed to mute audio:', error);
    }
  }

  unmuteAudio(session: MeetingSession): void {
    try {
      session.audioVideo.realtimeUnmuteLocalAudio();
      debug.log('✓ Audio unmuted');
    } catch (error) {
      console.error('✗ Failed to unmute audio:', error);
    }
  }
}

export const chimeService = new ChimeService();
